/**
 * QMS data layer — all calls go to /api/QMS/* (ClinicalTrialAPI QMSController → DMSSetUp).
 */
import apiClient from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import { getQmsScreen } from "./qmsScreenConfig";
import {
  apiMessage,
  firstSuccessStoryRow,
  getApiTable,
  getApiTableRow,
  getApiData,
  isApiSuccess,
  pickApiRowValue,
} from "./qmsApiUtils";
import {
  hasSavedCertificateTemplate,
  resolveCertificateTemplateContent,
} from "./qmsCertificateTemplateTags";
import { resolveQmsDashboardDateRange } from "./qmsDashboardUtils";

const E = API_ENDPOINTS.QMS;

const ENDPOINT_BY_KEY = {
  DOCUMENT_TYPE: E.DOCUMENT_TYPE_ACTIVITY,
  DOCUMENT_CATEGORY: E.DOCUMENT_CATEGORY_ACTIVITY,
  MY_VAULT: E.MY_VAULT_ACTIVITY,
  MY_VAULT_UPLOAD: E.MY_VAULT_UPLOAD,
  SHARED_WITH_ME: E.SHARED_WITH_ME_ACTIVITY,
  DOCUMENT_COMMENT: E.DOCUMENT_COMMENT_ACTIVITY,
  CHECK_IN_OUT: E.CHECK_IN_CHECK_OUT_ACTIVITY,
  DOCUMENT_QUIZZ: E.DOCUMENT_QUIZZ_ACTIVITY,
  MY_LEARNING: E.MY_LEARNING_ACTIVITY,
  DASHBOARD: E.DASHBOARD_ACTIVITY,
  CHANGE_REQUEST: E.CHANGE_REQUEST_ACTIVITY,
  TRACKER: E.TRACKER_ACTIVITY,
  CERTIFICATE_TEMPLATE: E.CERTIFICATE_TEMPLATE_ACTIVITY,
    DOCUMENT_LIBRARY_V2: E.DOCUMENT_LIBRARY_V2_ACTIVITY,
    MY_LEARNING_V2: E.MY_LEARNING_V2_ACTIVITY,
    QUESTION_BANK_V2: E.QUESTION_BANK_V2_ACTIVITY,
  QUIZ_V2: E.QUIZ_V2_ACTIVITY,
};

export function buildQmsBasePayload(userData, overrides = {}) {
  return {
    OrganizationId: userData?.OrganizationId || "",
    CreatedBy: userData?.StaffId || "",
    ActionType: "",
    ...overrides,
  };
}

export async function postQms(endpointKey, payload) {
  const url = ENDPOINT_BY_KEY[endpointKey];
  if (!url) {
    throw new Error(`Unknown QMS endpoint key: ${endpointKey}`);
  }
  return apiClient.post(url, payload);
}

/** Screen connectivity check — uses each screen's probe ActionType (no static UI data). */
export async function probeQmsScreen(screenKey, userData) {
  const screen = getQmsScreen(screenKey);
  if (!screen || !userData?.OrganizationId) {
    return { ok: false, message: "Missing screen or organization context." };
  }
  const payload = buildQmsBasePayload(userData, {
    ActionType: screen.probeActionType,
    ...(screen.probePayload || {}),
  });
  const response = await postQms(screen.endpointKey, payload);
  return {
    ok: Number(response?.success) === 1,
    message: response?.message || "",
    response,
  };
}

// —— Document type ——
export function fetchDocumentTypes(userData) {
  return postQms(
    "DOCUMENT_TYPE",
    buildQmsBasePayload(userData, { ActionType: "GetAllDocumentType" }),
  );
}

export function saveDocumentType(userData, body) {
  return postQms("DOCUMENT_TYPE", buildQmsBasePayload(userData, body));
}

export function deleteDocumentType(userData, documentTypeId) {
  return postQms(
    "DOCUMENT_TYPE",
    buildQmsBasePayload(userData, {
      ActionType: "DeleteDocumentType",
      DocumentTypeId: documentTypeId,
    }),
  );
}

// —— Document category ——
export function fetchDocumentCategories(userData) {
  return postQms(
    "DOCUMENT_CATEGORY",
    buildQmsBasePayload(userData, { ActionType: "GetAllDocumentCategory" }),
  );
}

export function postDocumentCategory(userData, body) {
  return postQms("DOCUMENT_CATEGORY", buildQmsBasePayload(userData, body));
}

export function deleteDocumentCategory(userData, documentCategoryId) {
  return postQms(
    "DOCUMENT_CATEGORY",
    buildQmsBasePayload(userData, {
      ActionType: "DeleteDocumentCategory",
      DocumentCategoryId: documentCategoryId,
    }),
  );
}

// —— My vault & library (ActionType-driven) ——
export function postMyVault(userData, body) {
  return postQms("MY_VAULT", buildQmsBasePayload(userData, body));
}

export function fetchMyVaultDocuments(userData) {
  return postMyVault(userData, { ActionType: "GetMyVaultDocuments" });
}

export function fetchMyVaultDocumentById(userData, documentId) {
  return postMyVault(userData, {
    ActionType: "GetMyVaultDocumentByDocumentId",
    DocumentId: documentId,
  });
}

export function fetchDocumentsForPublish(userData) {
  return postMyVault(userData, { ActionType: "GetDocumentsForPublish" });
}

export function fetchDocumentControlList(userData) {
  return postMyVault(userData, { ActionType: "GetDocumentControlList" });
}

export async function archiveDocumentControl(userData, { documentId, comments = "" }) {
  const response = await postMyVault(userData, {
    ActionType: "ArchiveDocument",
    DocumentId: documentId,
    Comments: comments,
  });
  if (!isApiSuccess(response)) {
    throw new Error(apiMessage(response) || "Archive failed.");
  }
  return response;
}

export async function unarchiveDocumentControl(userData, { documentId, comments = "" }) {
  const response = await postMyVault(userData, {
    ActionType: "UnarchiveDocument",
    DocumentId: documentId,
    Comments: comments,
  });
  if (!isApiSuccess(response)) {
    throw new Error(apiMessage(response) || "Unarchive failed.");
  }
  return response;
}

export async function transferDocumentOwnership(userData, { documentIds, newOwnerId, comments = "" }) {
  const response = await postMyVault(userData, {
    ActionType: "DocumentTransferToNewOwner",
    DocumentIds: documentIds,
    NewOwnerId: newOwnerId,
    Comments: comments,
  });
  if (!isApiSuccess(response)) {
    throw new Error(apiMessage(response) || "Ownership transfer failed.");
  }
  return response;
}

export function publishVaultDocument(userData, body) {
  return postMyVault(userData, {
    ActionType: "PublishDocument",
    ...body,
  });
}

export function saveMyLearningStaff(userData, { documentId, staffIds }) {
  return postMyVault(userData, {
    ActionType: "SaveMyLearningStaff",
    DocumentId: documentId,
    MyLearningAccess: staffIds,
  });
}

export function saveDocumentLibraryStaff(userData, { documentId, staffIds }) {
  return postMyVault(userData, {
    ActionType: "SaveDocumentLibraryStaff",
    DocumentId: documentId,
    DocumentLibraryAccess: staffIds,
  });
}

export function removeMyLearningStaff(userData, { documentId, staffIds }) {
  return postMyVault(userData, {
    ActionType: "RemoveMyLearningStaff",
    DocumentId: documentId,
    MyLearningAccess: staffIds,
  });
}

export function addMyVaultDocumentLibraryStaff(userData, { documentId, staffIds }) {
  return postMyVault(userData, {
    ActionType: "AddDocumentLibraryStaff",
    DocumentId: documentId,
    DocumentLibraryAccess: staffIds,
  });
}

export function removeMyVaultDocumentLibraryStaff(userData, { documentId, staffIds }) {
  return postMyVault(userData, {
    ActionType: "RemoveDocumentLibraryStaff",
    DocumentId: documentId,
    DocumentLibraryAccess: staffIds,
  });
}

export function removeAllDocumentLibraryStaff(userData, documentId) {
  return postMyVault(userData, {
    ActionType: "RemoveDocumentLibraryStaffAll",
    DocumentId: documentId,
  });
}

export function bindVaultDocumentQuiz(userData, { documentId, quizId }) {
  return postMyVault(userData, {
    ActionType: "BindDocumentQuiz_V2",
    DocumentId: documentId,
    QuizId: quizId,
  });
}

export function unbindVaultDocumentQuiz(userData, documentId) {
  return postMyVault(userData, {
    ActionType: "UnbindDocumentQuiz_V2",
    DocumentId: documentId,
  });
}

export function fetchDocumentCategoryUniqueNumber(userData, documentCategoryId) {
  return postDocumentCategory(userData, {
    ActionType: "GetDocumentCategoryUniqueNumberById",
    DocumentCategoryId: documentCategoryId,
  });
}

export function uploadVaultDocument(userData, body) {
  return postQms("MY_VAULT_UPLOAD", buildQmsBasePayload(userData, body));
}

export function vaultCheckOutDocument(userData, { documentId, comments = "" }) {
  return postCheckInCheckOut(userData, {
    ActionType: "CheckOutDocument",
    DocumentId: documentId,
    Comments: comments,
    CheckOutStatus: 1,
  });
}

export function vaultCancelCheckOutDocument(userData, { documentId, comments = "" }) {
  return postCheckInCheckOut(userData, {
    ActionType: "CancelCheckOutDocument",
    DocumentId: documentId,
    Comments: comments,
    CheckOutStatus: 3,
  });
}

export function vaultCheckInDocument(userData, body) {
  return uploadVaultDocument(userData, {
    ActionType: "CheckInDocument",
    ...body,
  });
}

export function saveVaultReviewers(userData, { documentId, reviewers, actionType }) {
  return postMyVault(userData, {
    ActionType: actionType,
    DocumentId: documentId,
    Reviewers: reviewers,
  });
}

export function removeVaultReviewer(userData, { documentId, reviewerId }) {
  return postMyVault(userData, {
    ActionType: "RemoveReviewer",
    DocumentId: documentId,
    Reviewers: reviewerId,
  });
}

export function vaultReviewComplete(userData, documentId, comments = "") {
  return postMyVault(userData, {
    ActionType: "ReviewComplete",
    DocumentId: documentId,
    Comments: comments || "",
  });
}

export function saveVaultApprovers(userData, { documentId, approvers, actionType }) {
  return postMyVault(userData, {
    ActionType: actionType,
    DocumentId: documentId,
    Reviewers: approvers,
  });
}

export function removeVaultApprover(userData, { documentId, approverId }) {
  return postMyVault(userData, {
    ActionType: "RemoveApprover",
    DocumentId: documentId,
    Reviewers: approverId,
  });
}

export function vaultApprovalComplete(userData, documentId, comments = "") {
  return postMyVault(userData, {
    ActionType: "AppovalComplete",
    DocumentId: documentId,
    Comments: comments || "",
  });
}

export function saveVaultAssociatedDocuments(userData, { documentId, associatedDocuments }) {
  return postMyVault(userData, {
    ActionType: "SaveAssociatedDocuments",
    DocumentId: documentId,
    AssociatedDocuments: associatedDocuments,
  });
}

export function removeVaultAssociatedDocument(userData, { documentId, associatedDocumentId }) {
  return postMyVault(userData, {
    ActionType: "RemoveAssociatedDocuments",
    DocumentId: documentId,
    AssociatedDocuments: associatedDocumentId,
  });
}

export function fetchDocumentComments(userData, documentId) {
  return postDocumentComment(userData, {
    ActionType: "GetCommentByDocumentId",
    DocumentId: documentId,
  });
}

export function saveDocumentComment(userData, body) {
  return postDocumentComment(userData, {
    ActionType: "SaveComment",
    CommentId: "",
    CommentPriority: "Medium",
    ...body,
  });
}

export function editDocumentComment(userData, body) {
  return postDocumentComment(userData, {
    ActionType: "EditComment",
    ...body,
  });
}

export function deleteDocumentComment(userData, body) {
  return postDocumentComment(userData, {
    ActionType: "DeleteComment",
    ...body,
  });
}

export function fetchDocumentQuizByDocumentId(userData, documentId) {
  return postDocumentQuizz(userData, {
    ActionType: "GetQuizzByDocumentId",
    DocumentId: documentId,
  });
}

export function fetchDocumentQuizQuestion(userData, { documentId, questionId }) {
  return postDocumentQuizz(userData, {
    ActionType: "GetQuestionByQuestionId",
    DocumentId: documentId,
    QuestionId: questionId,
  });
}

export function saveDocumentQuizQuestion(userData, body) {
  return postDocumentQuizz(userData, body);
}

export function deleteDocumentQuizQuestion(userData, { documentId, questionId }) {
  return postDocumentQuizz(userData, {
    ActionType: "DeleteQuestion",
    DocumentId: documentId,
    QuestionId: questionId,
  });
}

export function postSharedWithMe(userData, body) {
  return postQms("SHARED_WITH_ME", buildQmsBasePayload(userData, body));
}

export function fetchSharedWithMeDocuments(userData) {
  return postSharedWithMe(userData, { ActionType: "GetSharedWithMeDocuments" });
}

export function fetchSharedWithMeDocumentById(userData, documentId) {
  return postSharedWithMe(userData, {
    ActionType: "GetSHAREDWITHMEDocumentByDocumentId",
    DocumentId: documentId,
  });
}

export function submitSharedWithMeReview(userData, { documentId, reviewerStatus, comments }) {
  return postSharedWithMe(userData, {
    ActionType: "ReviewSubmitByReviewer",
    DocumentId: documentId,
    ReviewerStatus: reviewerStatus,
    Comments: comments || "",
  });
}

export function submitSharedWithMeApproval(userData, { documentId, approverStatus, comments }) {
  return postSharedWithMe(userData, {
    ActionType: "ApprovalSubmitByApprover",
    DocumentId: documentId,
    ApproverStatus: approverStatus,
    Comments: comments || "",
  });
}

export function postMyLearning(userData, body) {
  return postQms("MY_LEARNING", buildQmsBasePayload(userData, body));
}

export function postDashboard(userData, body) {
  return postQms("DASHBOARD", buildQmsBasePayload(userData, body));
}

export async function fetchQmsComplianceReport(userData) {
  const response = await postDashboard(userData, {
    ActionType: "GetComplianceReportData",
  });
  if (!isApiSuccess(response)) {
    throw new Error(apiMessage(response) || "Compliance report request failed.");
  }
  return getApiTable(response, "SuccessStory");
}

async function fetchQmsDashboardReport(userData, actionType, dateRange = {}, options = {}) {
  const { requireDateRange = false } = options;
  const isAllTime = !dateRange?.preset || dateRange.preset === "All Time";
  const payload = { ActionType: actionType };

  if (requireDateRange || !isAllTime || dateRange?.startDate || dateRange?.endDate) {
    const resolved = resolveQmsDashboardDateRange({
      preset: isAllTime && requireDateRange ? "Last Year" : dateRange.preset || dateRange.dateRange,
      startDate: dateRange.startDate || dateRange.dateFrom,
      endDate: dateRange.endDate || dateRange.dateTo,
    });
    if (resolved.startDate && resolved.endDate) {
      payload.StartDate = resolved.startDate;
      payload.EndDate = resolved.endDate;
    }
  }

  const response = await postDashboard(userData, payload);
  if (!isApiSuccess(response)) {
    throw new Error(apiMessage(response) || `${actionType} request failed.`);
  }
  return getApiTable(response, "SuccessStory");
}

export async function fetchQmsPendingApprovalReport(userData, dateRange) {
  return fetchQmsDashboardReport(userData, "GetPendingApprovalReport", dateRange);
}

export async function fetchQmsPendingReviewReport(userData, dateRange) {
  return fetchQmsDashboardReport(userData, "GetPendingReviewReport", dateRange);
}

export async function fetchQmsDocumentCreationTrendReport(userData, dateRange) {
  return fetchQmsDashboardReport(userData, "GetDocumentCreationTrendReport", dateRange, {
    requireDateRange: true,
  });
}

export async function fetchQmsDocumentArchivingTrendReport(userData, dateRange) {
  return fetchQmsDashboardReport(userData, "GetDocumentArchivingTrendReport", dateRange, {
    requireDateRange: true,
  });
}

export async function fetchQmsArchivedDocumentsReport(userData, dateRange) {
  return fetchQmsDashboardReport(userData, "GetArchivedDocumentsReport", dateRange);
}

export async function fetchQmsTransferOwnershipReport(userData) {
  const response = await postDashboard(userData, {
    ActionType: "GetTransferOwnershipData",
  });
  if (!isApiSuccess(response)) {
    throw new Error(apiMessage(response) || "Transfer ownership report failed.");
  }
  return getApiTable(response, "SuccessStory");
}

export async function fetchQmsTrackerReport(userData) {
  const response = await postTracker(userData, { ActionType: "GetTrackerData" });
  if (!isApiSuccess(response)) {
    throw new Error(apiMessage(response) || "Tracker report failed.");
  }
  return getApiTable(response, "SuccessStory");
}

export async function fetchQmsReadingListReport(userData) {
  const response = await postMyVault(userData, { ActionType: "GetReadingList" });
  if (!isApiSuccess(response)) {
    throw new Error(apiMessage(response) || "Reading list report failed.");
  }
  return getApiTable(response, "SuccessStory");
}

export async function fetchQmsArchiveDocumentsReport(userData) {
  const response = await postMyVault(userData, { ActionType: "GetArchiveDocumentList" });
  if (!isApiSuccess(response)) {
    throw new Error(apiMessage(response) || "Archive documents report failed.");
  }
  return getApiTable(response, "SuccessStory");
}

export async function fetchQmsChangeRequestsReport(userData) {
  const response = await postChangeRequest(userData, { ActionType: "RequestsForMe" });
  if (!isApiSuccess(response)) {
    throw new Error(apiMessage(response) || "Change requests report failed.");
  }
  return getApiTable(response, "SuccessStory");
}

export async function fetchQmsDashboard(userData, { startDate, endDate, preset, ...rangeValue } = {}) {
  const resolved = resolveQmsDashboardDateRange({
    preset,
    startDate,
    endDate,
    ...rangeValue,
  });

  if (!resolved.startDate || !resolved.endDate) {
    throw new Error("Select a valid date range.");
  }

  const response = await postDashboard(userData, {
    ActionType: "GetDMSDashboard",
    StartDate: resolved.startDate,
    EndDate: resolved.endDate,
  });

  if (!isApiSuccess(response)) {
    throw new Error(apiMessage(response) || "Dashboard request failed.");
  }

  return response;
}

export function postChangeRequest(userData, body) {
  return postQms("CHANGE_REQUEST", buildQmsBasePayload(userData, body));
}

export function postTracker(userData, body) {
  return postQms("TRACKER", buildQmsBasePayload(userData, body));
}

export function postCertificateTemplate(userData, body) {
  return postQms("CERTIFICATE_TEMPLATE", buildQmsBasePayload(userData, body));
}

export async function fetchCertificateTemplate(userData) {
  const response = await postCertificateTemplate(userData, {
    ActionType: "GetCertificateTemplate",
  });
  const row =
    getApiTableRow(response, "SuccessStory", "CertificateTemplate") ||
    firstSuccessStoryRow(response) ||
    getApiTable(response, "SuccessStory")[0];
  const savedContent = String(
    pickApiRowValue(row, "TemplateContent", "templateContent") || "",
  );
  return {
    certificateTemplateId:
      pickApiRowValue(row, "CertificateTemplateId", "certificateTemplateId") || "",
    templateContent: resolveCertificateTemplateContent(savedContent),
    isUsingDefaultTemplate: !hasSavedCertificateTemplate(savedContent),
  };
}

export async function saveCertificateTemplate(userData, { certificateTemplateId, templateContent }) {
  const actionType = certificateTemplateId
    ? "UpdateCertificateTemplate"
    : "SaveCertificateTemplate";
  const response = await postCertificateTemplate(userData, {
    ActionType: actionType,
    CertificateTemplateId: certificateTemplateId || "",
    TemplateContent: templateContent || "",
  });
  const procRow = firstSuccessStoryRow(response);
  if (!isApiSuccess(response, procRow)) {
    throw new Error(apiMessage(response, procRow) || "Failed to save certificate template.");
  }
  const row = getApiTableRow(response, "SuccessStory") || procRow || {};
  return {
    success: true,
    message: apiMessage(response, procRow) || "Certificate template saved.",
    certificateTemplateId:
      pickApiRowValue(row, "CertificateTemplateId", "certificateTemplateId") ||
      certificateTemplateId ||
      "",
    templateContent: String(
      pickApiRowValue(row, "TemplateContent", "templateContent") || templateContent || "",
    ),
  };
}

export function postDocumentComment(userData, body) {
  return postQms("DOCUMENT_COMMENT", buildQmsBasePayload(userData, body));
}

export function postCheckInCheckOut(userData, body) {
  return postQms("CHECK_IN_OUT", buildQmsBasePayload(userData, body));
}

export function postDocumentQuizz(userData, body) {
  return postQms("DOCUMENT_QUIZZ", buildQmsBasePayload(userData, body));
}

// —— Document library V2 (fast path — QMS_DocLibrary_StaffAssignment) ——
export function postDocumentLibraryV2(userData, body) {
  return postQms(
    "DOCUMENT_LIBRARY_V2",
    buildQmsBasePayload(userData, {
      Roles: userData?.Role || "",
      ...body,
    }),
  );
}

export function fetchDocumentLibraryPage(
  userData,
  {
    pageNumber = 1,
    pageSize = 25,
    searchText = "",
    documentId = "",
    filterDocumentTypeIds = "",
    filterDocumentCategoryIds = "",
    filterIsActive = "",
    filterFileTypes = "",
    isReadingMandatory = null,
    isQuizApplicable = null,
    filterCreatedBy = "",
    filterReviewDueDateFrom = "",
    filterReviewDueDateTo = "",
    filterPublishedBy = "",
    filterCreatedDate = "",
    filterPublishedOn = "",
    filterDocumentIdNumber = "",
    filterCreatedDateFrom = "",
    filterCreatedDateTo = "",
    filterPublishedOnFrom = "",
    filterPublishedOnTo = "",
    filterLearningProgress = "",
  } = {},
) {
  return postDocumentLibraryV2(userData, {
    ActionType: "GetDocumentLibraryDocuments_V2",
    PageNumber: pageNumber,
    PageSize: pageSize,
    SearchText: searchText,
    DocumentId: documentId,
    FilterDocumentTypeIds: filterDocumentTypeIds,
    FilterDocumentCategoryIds: filterDocumentCategoryIds,
    FilterIsActive: filterIsActive,
    FilterFileTypes: filterFileTypes,
    IsReadingMandatory: isReadingMandatory,
    IsQuizApplicable: isQuizApplicable,
    FilterCreatedBy: filterCreatedBy,
    FilterReviewDueDateFrom: filterReviewDueDateFrom,
    FilterReviewDueDateTo: filterReviewDueDateTo,
    FilterPublishedBy: filterPublishedBy,
    FilterCreatedDate: filterCreatedDate,
    FilterPublishedOn: filterPublishedOn,
    FilterDocumentIdNumber: filterDocumentIdNumber,
    FilterCreatedDateFrom: filterCreatedDateFrom,
    FilterCreatedDateTo: filterCreatedDateTo,
    FilterPublishedOnFrom: filterPublishedOnFrom,
    FilterPublishedOnTo: filterPublishedOnTo,
    FilterLearningProgress: filterLearningProgress,
  });
}

export function fetchDocumentLibraryDocumentById(userData, documentId) {
  return fetchDocumentLibraryPage(userData, {
    pageNumber: 1,
    pageSize: 1,
    documentId,
    filterIsActive: "",
  });
}

/** Vault details with Document Library fallback when URL is missing (Document Control / org viewers). */
export async function fetchWorkspaceDocumentById(userData, documentId) {
  const vaultResponse = await fetchMyVaultDocumentById(userData, documentId);
  const vaultInfo = getApiTable(vaultResponse, "DocumentInfo")[0] || {};
  const vaultUrl = String(pickApiRowValue(vaultInfo, "DocumentURL", "documentUrl") || "").trim();
  if (vaultUrl) {
    return vaultResponse;
  }

  try {
    const libraryResponse = await fetchDocumentLibraryDocumentById(userData, documentId);
    const libDoc = getApiTable(libraryResponse, "DocumentLibraryDocuments")[0];
    const libUrl = String(pickApiRowValue(libDoc, "DocumentURL", "documentUrl") || "").trim();
    if (!libUrl) {
      return vaultResponse;
    }

    const data = getApiData(vaultResponse) || {};
    const mergedInfo = {
      ...vaultInfo,
      ...(libDoc || {}),
      DocumentURL: pickApiRowValue(libDoc, "DocumentURL", "documentUrl"),
      DocumentExtension:
        pickApiRowValue(libDoc, "DocumentExtension", "documentExtension") ||
        pickApiRowValue(vaultInfo, "DocumentExtension", "documentExtension"),
    };

    return {
      ...vaultResponse,
      data: {
        ...data,
        DocumentInfo: [mergedInfo],
      },
    };
  } catch {
    return vaultResponse;
  }
}

export function fetchDocumentLibraryStaff(userData, documentId) {
  return postDocumentLibraryV2(userData, {
    ActionType: "GetDocumentLibraryStaff_V2",
    DocumentId: documentId,
  });
}

export function fetchDocumentLibraryStaffPicker(userData, documentId) {
  return postDocumentLibraryV2(userData, {
    ActionType: "GetDocumentLibraryStaffPicker_V2",
    DocumentId: documentId,
  });
}

export function assignDocumentLibraryStaff(userData, documentId, staffId, comments = "") {
  return postDocumentLibraryV2(userData, {
    ActionType: "AssignLibraryStaff_V2",
    DocumentId: documentId,
    DocumentLibraryAccess: staffId,
    Comments: comments,
  });
}

export function removeDocumentLibraryStaff(userData, documentId, staffId, comments = "") {
  return postDocumentLibraryV2(userData, {
    ActionType: "RemoveLibraryStaff_V2",
    DocumentId: documentId,
    DocumentLibraryAccess: staffId,
    Comments: comments,
  });
}

/** User Management — published documents + library assignment for one staff member. */
export function fetchStaffDocumentLibraryDocuments(
  userData,
  targetStaffId,
  {
    pageNumber = 1,
    pageSize = 25,
    searchText = "",
    filterDocumentIdNumber = "",
    assignmentFilter = "all",
    filterPublishedOnFrom = "",
    filterPublishedOnTo = "",
  } = {},
) {
  return postDocumentLibraryV2(userData, {
    ActionType: "GetStaffDocumentLibraryDocuments_V2",
    TargetStaffId: targetStaffId,
    SearchText: searchText,
    PageNumber: pageNumber,
    PageSize: pageSize,
    FilterDocumentIdNumber: filterDocumentIdNumber,
    AssignmentFilter: assignmentFilter,
    FilterPublishedOnFrom: filterPublishedOnFrom,
    FilterPublishedOnTo: filterPublishedOnTo,
  });
}

// —— My Learning V2 (fast path — QMS_MyLearning_StaffAssignment) ——
export function postMyLearningV2(userData, body) {
  return postQms(
    "MY_LEARNING_V2",
    buildQmsBasePayload(userData, {
      Roles: userData?.Role || "",
      ...body,
    }),
  );
}

export function fetchMyLearningPage(
  userData,
  {
    pageNumber = 1,
    pageSize = 25,
    searchText = "",
    documentId = "",
    isReadingMandatory = null,
    isQuizApplicable = null,
    isAcknowledged = null,
    isPassed = null,
    filterDocumentTypeIds = "",
    filterDocumentCategoryIds = "",
    filterFileTypes = "",
    filterPublishedOnFrom = "",
    filterPublishedOnTo = "",
    filterDocumentIdNumber = "",
  } = {},
) {
  return postMyLearningV2(userData, {
    ActionType: "GetMyLearningDocuments_V2",
    PageNumber: pageNumber,
    PageSize: pageSize,
    SearchText: searchText,
    DocumentId: documentId,
    IsReadingMandatory: isReadingMandatory,
    IsQuizApplicable: isQuizApplicable,
    IsAcknowledged: isAcknowledged,
    IsPassed: isPassed,
    FilterDocumentTypeIds: filterDocumentTypeIds,
    FilterDocumentCategoryIds: filterDocumentCategoryIds,
    FilterFileTypes: filterFileTypes,
    FilterPublishedOnFrom: filterPublishedOnFrom,
    FilterPublishedOnTo: filterPublishedOnTo,
    FilterDocumentIdNumber: filterDocumentIdNumber,
  });
}

export function fetchMyLearningsHub(
  userData,
  {
    pageNumber = 1,
    pageSize = 25,
    searchText = "",
    learningStatus = "All",
    filterDocumentIdNumber = "",
    filterReviewDueDateFrom = "",
    filterReviewDueDateTo = "",
    filterPublishedOnFrom = "",
    filterPublishedOnTo = "",
    filterCompletedOnFrom = "",
    filterCompletedOnTo = "",
    isReadingMandatory = null,
    isQuizApplicable = null,
  } = {},
) {
  return postMyLearningV2(userData, {
    ActionType: "GetMyLearningsHub_V2",
    PageNumber: pageNumber,
    PageSize: pageSize,
    SearchText: searchText,
    LearningStatus: learningStatus,
    FilterDocumentIdNumber: filterDocumentIdNumber,
    FilterReviewDueDateFrom: filterReviewDueDateFrom,
    FilterReviewDueDateTo: filterReviewDueDateTo,
    FilterPublishedOnFrom: filterPublishedOnFrom,
    FilterPublishedOnTo: filterPublishedOnTo,
    FilterCompletedOnFrom: filterCompletedOnFrom,
    FilterCompletedOnTo: filterCompletedOnTo,
    IsReadingMandatory: isReadingMandatory,
    IsQuizApplicable: isQuizApplicable,
  });
}

export function fetchMyLearningDocumentHub(userData, documentId) {
  return postMyLearningV2(userData, {
    ActionType: "GetMyLearningDocumentHub_V2",
    DocumentId: documentId,
  });
}

export function acknowledgeMyLearningDocument(userData, { documentId, passed = false, comments = "", userOptions = "", quizId = "" }) {
  return postMyLearningV2(userData, {
    ActionType: "SubmitDocumentAcknowledgement_V2",
    DocumentId: documentId,
    Passed: passed,
    Comments: comments,
    UserOptions: userOptions,
    QuizId: quizId,
  });
}

export function fetchMyLearningStaffPicker(userData, documentId) {
  return postMyLearningV2(userData, {
    ActionType: "GetMyLearningStaffPicker_V2",
    DocumentId: documentId,
  });
}

export function fetchMyLearningStaff(userData, documentId) {
  return postMyLearningV2(userData, {
    ActionType: "GetMyLearningStaff_V2",
    DocumentId: documentId,
  });
}

export function sendMyLearningReminder(userData, documentId, staffIds) {
  const access =
    staffIds instanceof Set
      ? [...staffIds].map(String).filter(Boolean).join(",")
      : Array.isArray(staffIds)
        ? staffIds.map((id) => String(id)).filter(Boolean).join(",")
        : String(staffIds || "");

  return postMyLearningV2(userData, {
    ActionType: "SendMyLearningReminder_V2",
    DocumentId: documentId,
    MyLearningAccess: access,
  });
}

export function assignMyLearningStaff(userData, documentId, staffId, comments = "") {
  return postMyLearningV2(userData, {
    ActionType: "AssignMyLearningStaff_V2",
    DocumentId: documentId,
    MyLearningAccess: staffId,
    Comments: comments,
  });
}

export function removeMyLearningStaffV2(userData, documentId, staffId, comments = "") {
  return postMyLearningV2(userData, {
    ActionType: "RemoveMyLearningStaff_V2",
    DocumentId: documentId,
    MyLearningAccess: staffId,
    Comments: comments,
  });
}

/** User Management — published documents + My Learning assignment for one staff member. */
export function fetchStaffMyLearningDocuments(
  userData,
  targetStaffId,
  {
    pageNumber = 1,
    pageSize = 25,
    searchText = "",
    filterDocumentIdNumber = "",
    assignmentFilter = "all",
    filterPublishedOnFrom = "",
    filterPublishedOnTo = "",
  } = {},
) {
  return postMyLearningV2(userData, {
    ActionType: "GetStaffMyLearningDocuments_V2",
    TargetStaffId: targetStaffId,
    SearchText: searchText,
    PageNumber: pageNumber,
    PageSize: pageSize,
    FilterDocumentIdNumber: filterDocumentIdNumber,
    AssignmentFilter: assignmentFilter,
    FilterPublishedOnFrom: filterPublishedOnFrom,
    FilterPublishedOnTo: filterPublishedOnTo,
  });
}

function getFirstAvailableTable(data, candidates = []) {
  if (!data || typeof data !== "object") return null;
  const keys = Object.keys(data);
  for (const key of candidates) {
    const val = data[key];
    if (Array.isArray(val)) return val;
    const ci = keys.find((k) => k.toLowerCase() === String(key || "").toLowerCase());
    if (ci && Array.isArray(data[ci])) return data[ci];
  }
  if (Array.isArray(data?.Tables)) {
    for (const name of candidates) {
      const table = data.Tables.find(
        (t) =>
          String(t?.TableName || t?.Name || t?.tableName || "")
            .toLowerCase() === String(name || "").toLowerCase(),
      );
      if (Array.isArray(table?.Rows) && table.Rows.length) return table.Rows;
    }
    const first = data.Tables.find((t) => Array.isArray(t?.Rows) && t.Rows.length);
    if (first?.Rows) return first.Rows;
  }
  const firstArrayKey = keys.find((k) => Array.isArray(data[k]));
  return firstArrayKey ? data[firstArrayKey] : null;
}

function resolveQmsEmailTemplate(template, defaultValue) {
  const custom = String(template ?? "").trim();
  if (custom) return custom;
  return String(defaultValue ?? "").trim();
}

function getApiDataset(response) {
  if (!response || typeof response !== "object") return null;
  const d = response.data ?? response.Data;
  return d && typeof d === "object" ? d : null;
}

function qmsConfigStr(row, pascalKey, camelKey) {
  const v = row?.[pascalKey] ?? row?.[camelKey];
  return v == null ? "" : String(v);
}

function buildQmsConfigFallback() {
  return {
    libraryStaffAssignEmailSubjectTemplate: "",
    libraryStaffAssignEmailBodyTemplate: "",
    libraryStaffAssignEmailSubjectDefault:
      "New document in your library — {{DocumentTitle}}",
    libraryStaffAssignEmailBodyDefault:
      "<p>Hello {{StaffName}},</p><p>A new document has been added to your document library:</p><p><strong>{{DocumentTitle}}</strong> ({{DocumentCode}})</p><p>{{DocumentOpenLink}}</p><p>Regards,<br />{{OrganizationName}}</p>",
    libraryStaffRemoveEmailSubjectTemplate: "",
    libraryStaffRemoveEmailBodyTemplate: "",
    libraryStaffRemoveEmailSubjectDefault:
      "Document removed from your library — {{DocumentTitle}}",
    libraryStaffRemoveEmailBodyDefault:
      "<p>Hello {{StaffName}},</p><p>Your access to the following document has been removed from the document library:</p><p><strong>{{DocumentTitle}}</strong> ({{DocumentCode}})</p><p>If you believe this is incorrect, please contact your QMS administrator.</p><p>Regards,<br />{{OrganizationName}}</p>",
    reviewWorkflowEmailSubjectTemplate: "",
    reviewWorkflowEmailBodyTemplate: "",
    reviewWorkflowEmailSubjectDefault: "Document for review — {{DocumentTitle}}",
    reviewWorkflowEmailBodyDefault:
      "<p>Hello {{StaffName}},</p><p>{{SentByName}} has sent a document to you for review:</p><p><strong>{{DocumentTitle}}</strong> ({{DocumentCode}})</p><p>{{DocumentOpenLink}}</p><p>Regards,<br />{{OrganizationName}}</p>",
    approveWorkflowEmailSubjectTemplate: "",
    approveWorkflowEmailBodyTemplate: "",
    approveWorkflowEmailSubjectDefault: "Document for approval — {{DocumentTitle}}",
    approveWorkflowEmailBodyDefault:
      "<p>Hello {{StaffName}},</p><p>{{SentByName}} has sent a document to you for approval:</p><p><strong>{{DocumentTitle}}</strong> ({{DocumentCode}})</p><p>{{DocumentOpenLink}}</p><p>Regards,<br />{{OrganizationName}}</p>",
    myLearningStaffAssignEmailSubjectTemplate: "",
    myLearningStaffAssignEmailBodyTemplate: "",
    myLearningStaffAssignEmailSubjectDefault:
      "New document in My Learning — {{DocumentTitle}}",
    myLearningStaffAssignEmailBodyDefault:
      "<p>Hello {{StaffName}},</p><p>You have a new document to read in My Learning:</p><p><strong>{{DocumentTitle}}</strong> ({{DocumentCode}})</p><p>{{DocumentOpenLink}}</p><p>Regards,<br />{{OrganizationName}}</p>",
    myLearningStaffRemoveEmailSubjectTemplate: "",
    myLearningStaffRemoveEmailBodyTemplate: "",
    myLearningStaffRemoveEmailSubjectDefault:
      "Document removed from My Learning — {{DocumentTitle}}",
    myLearningStaffRemoveEmailBodyDefault:
      "<p>Hello {{StaffName}},</p><p>Your access to the following document has been removed from My Learning:</p><p><strong>{{DocumentTitle}}</strong> ({{DocumentCode}})</p><p>If you believe this is incorrect, please contact your QMS administrator.</p><p>Regards,<br />{{OrganizationName}}</p>",
    transferOwnershipEmailSubjectTemplate: "",
    transferOwnershipEmailBodyTemplate: "",
    transferOwnershipEmailSubjectDefault:
      "Document ownership transferred - {{DocumentCount}} document(s) assigned to you",
    transferOwnershipEmailBodyDefault:
      '<div style="font-family:Segoe UI, Arial, sans-serif;font-size:14px;color:#334155;line-height:1.55;max-width:640px;"><p style="margin:0 0 14px;">Hello {{StaffName}},</p><p style="margin:0 0 14px;"><strong>{{TransferredByName}}</strong> has transferred ownership of <strong>{{DocumentCount}}</strong> document(s) to you in the Quality Management System. You are now the owner of these documents.</p><p style="margin:0 0 8px;font-weight:600;">Documents transferred to you</p>{{DocumentListHtml}}{{TransferReasonBlock}}<p style="margin:0 0 14px;">You can review and manage these documents in <strong>My Vault</strong>.</p><p style="margin:0 0 18px;">{{MyVaultOpenLink}}</p><p style="margin:0;color:#64748b;font-size:13px;">If you have questions about this transfer, please contact your QMS administrator or the person who performed the transfer.</p><p style="margin:18px 0 0;">Regards,<br /><strong>{{OrganizationName}}</strong></p></div>',
    libraryAutoAssignEnabled: false,
    libraryAutoAssignMode: "AllExceptExcluded",
  };
}

export async function fetchQmsConfig(userData) {
  try {
    const res = await apiClient.post(E.CONFIG_GET, buildQmsBasePayload(userData, { ActionType: "GetConfig" }));
    if (Number(res?.success) !== 1) {
      return buildQmsConfigFallback();
    }
    const ds = getApiDataset(res);
    const rows = getFirstAvailableTable(ds, ["QmsConfig", "qmsConfig", "Table", "Table1"]);
    if (!Array.isArray(rows) || !rows[0]) {
      return buildQmsConfigFallback();
    }
    const row = rows[0];
    const assignSubjTpl = qmsConfigStr(
      row,
      "LibraryStaffAssignEmailSubjectTemplate",
      "libraryStaffAssignEmailSubjectTemplate",
    );
    const assignBodyTpl = qmsConfigStr(
      row,
      "LibraryStaffAssignEmailBodyTemplate",
      "libraryStaffAssignEmailBodyTemplate",
    );
    const assignSubjDef = qmsConfigStr(
      row,
      "LibraryStaffAssignEmailSubjectDefault",
      "libraryStaffAssignEmailSubjectDefault",
    );
    const assignBodyDef = qmsConfigStr(
      row,
      "LibraryStaffAssignEmailBodyDefault",
      "libraryStaffAssignEmailBodyDefault",
    );
    const removeSubjTpl = qmsConfigStr(
      row,
      "LibraryStaffRemoveEmailSubjectTemplate",
      "libraryStaffRemoveEmailSubjectTemplate",
    );
    const removeBodyTpl = qmsConfigStr(
      row,
      "LibraryStaffRemoveEmailBodyTemplate",
      "libraryStaffRemoveEmailBodyTemplate",
    );
    const removeSubjDef = qmsConfigStr(
      row,
      "LibraryStaffRemoveEmailSubjectDefault",
      "libraryStaffRemoveEmailSubjectDefault",
    );
    const removeBodyDef = qmsConfigStr(
      row,
      "LibraryStaffRemoveEmailBodyDefault",
      "libraryStaffRemoveEmailBodyDefault",
    );
    const reviewSubjTpl = qmsConfigStr(
      row,
      "ReviewWorkflowEmailSubjectTemplate",
      "reviewWorkflowEmailSubjectTemplate",
    );
    const reviewBodyTpl = qmsConfigStr(
      row,
      "ReviewWorkflowEmailBodyTemplate",
      "reviewWorkflowEmailBodyTemplate",
    );
    const reviewSubjDef = qmsConfigStr(
      row,
      "ReviewWorkflowEmailSubjectDefault",
      "reviewWorkflowEmailSubjectDefault",
    );
    const reviewBodyDef = qmsConfigStr(
      row,
      "ReviewWorkflowEmailBodyDefault",
      "reviewWorkflowEmailBodyDefault",
    );
    const approveSubjTpl = qmsConfigStr(
      row,
      "ApproveWorkflowEmailSubjectTemplate",
      "approveWorkflowEmailSubjectTemplate",
    );
    const approveBodyTpl = qmsConfigStr(
      row,
      "ApproveWorkflowEmailBodyTemplate",
      "approveWorkflowEmailBodyTemplate",
    );
    const approveSubjDef = qmsConfigStr(
      row,
      "ApproveWorkflowEmailSubjectDefault",
      "approveWorkflowEmailSubjectDefault",
    );
    const approveBodyDef = qmsConfigStr(
      row,
      "ApproveWorkflowEmailBodyDefault",
      "approveWorkflowEmailBodyDefault",
    );
    const mlAssignSubjTpl = qmsConfigStr(
      row,
      "MyLearningStaffAssignEmailSubjectTemplate",
      "myLearningStaffAssignEmailSubjectTemplate",
    );
    const mlAssignBodyTpl = qmsConfigStr(
      row,
      "MyLearningStaffAssignEmailBodyTemplate",
      "myLearningStaffAssignEmailBodyTemplate",
    );
    const mlAssignSubjDef = qmsConfigStr(
      row,
      "MyLearningStaffAssignEmailSubjectDefault",
      "myLearningStaffAssignEmailSubjectDefault",
    );
    const mlAssignBodyDef = qmsConfigStr(
      row,
      "MyLearningStaffAssignEmailBodyDefault",
      "myLearningStaffAssignEmailBodyDefault",
    );
    const mlRemoveSubjTpl = qmsConfigStr(
      row,
      "MyLearningStaffRemoveEmailSubjectTemplate",
      "myLearningStaffRemoveEmailSubjectTemplate",
    );
    const mlRemoveBodyTpl = qmsConfigStr(
      row,
      "MyLearningStaffRemoveEmailBodyTemplate",
      "myLearningStaffRemoveEmailBodyTemplate",
    );
    const mlRemoveSubjDef = qmsConfigStr(
      row,
      "MyLearningStaffRemoveEmailSubjectDefault",
      "myLearningStaffRemoveEmailSubjectDefault",
    );
    const mlRemoveBodyDef = qmsConfigStr(
      row,
      "MyLearningStaffRemoveEmailBodyDefault",
      "myLearningStaffRemoveEmailBodyDefault",
    );
    const transferSubjTpl = qmsConfigStr(
      row,
      "TransferOwnershipEmailSubjectTemplate",
      "transferOwnershipEmailSubjectTemplate",
    );
    const transferBodyTpl = qmsConfigStr(
      row,
      "TransferOwnershipEmailBodyTemplate",
      "transferOwnershipEmailBodyTemplate",
    );
    const transferSubjDef = qmsConfigStr(
      row,
      "TransferOwnershipEmailSubjectDefault",
      "transferOwnershipEmailSubjectDefault",
    );
    const transferBodyDef = qmsConfigStr(
      row,
      "TransferOwnershipEmailBodyDefault",
      "transferOwnershipEmailBodyDefault",
    );
    return {
      libraryStaffAssignEmailSubjectTemplate: assignSubjTpl,
      libraryStaffAssignEmailBodyTemplate: assignBodyTpl,
      libraryStaffAssignEmailSubjectDefault: assignSubjDef,
      libraryStaffAssignEmailBodyDefault: assignBodyDef,
      libraryStaffRemoveEmailSubjectTemplate: removeSubjTpl,
      libraryStaffRemoveEmailBodyTemplate: removeBodyTpl,
      libraryStaffRemoveEmailSubjectDefault: removeSubjDef,
      libraryStaffRemoveEmailBodyDefault: removeBodyDef,
      reviewWorkflowEmailSubjectTemplate: reviewSubjTpl,
      reviewWorkflowEmailBodyTemplate: reviewBodyTpl,
      reviewWorkflowEmailSubjectDefault: reviewSubjDef,
      reviewWorkflowEmailBodyDefault: reviewBodyDef,
      approveWorkflowEmailSubjectTemplate: approveSubjTpl,
      approveWorkflowEmailBodyTemplate: approveBodyTpl,
      approveWorkflowEmailSubjectDefault: approveSubjDef,
      approveWorkflowEmailBodyDefault: approveBodyDef,
      myLearningStaffAssignEmailSubjectTemplate: mlAssignSubjTpl,
      myLearningStaffAssignEmailBodyTemplate: mlAssignBodyTpl,
      myLearningStaffAssignEmailSubjectDefault: mlAssignSubjDef,
      myLearningStaffAssignEmailBodyDefault: mlAssignBodyDef,
      myLearningStaffRemoveEmailSubjectTemplate: mlRemoveSubjTpl,
      myLearningStaffRemoveEmailBodyTemplate: mlRemoveBodyTpl,
      myLearningStaffRemoveEmailSubjectDefault: mlRemoveSubjDef,
      myLearningStaffRemoveEmailBodyDefault: mlRemoveBodyDef,
      transferOwnershipEmailSubjectTemplate: transferSubjTpl,
      transferOwnershipEmailBodyTemplate: transferBodyTpl,
      transferOwnershipEmailSubjectDefault: transferSubjDef,
      transferOwnershipEmailBodyDefault: transferBodyDef,
      libraryStaffAssignEmailSubjectDisplay: resolveQmsEmailTemplate(assignSubjTpl, assignSubjDef),
      libraryStaffAssignEmailBodyDisplay: resolveQmsEmailTemplate(assignBodyTpl, assignBodyDef),
      libraryStaffRemoveEmailSubjectDisplay: resolveQmsEmailTemplate(removeSubjTpl, removeSubjDef),
      libraryStaffRemoveEmailBodyDisplay: resolveQmsEmailTemplate(removeBodyTpl, removeBodyDef),
      reviewWorkflowEmailSubjectDisplay: resolveQmsEmailTemplate(reviewSubjTpl, reviewSubjDef),
      reviewWorkflowEmailBodyDisplay: resolveQmsEmailTemplate(reviewBodyTpl, reviewBodyDef),
      approveWorkflowEmailSubjectDisplay: resolveQmsEmailTemplate(approveSubjTpl, approveSubjDef),
      approveWorkflowEmailBodyDisplay: resolveQmsEmailTemplate(approveBodyTpl, approveBodyDef),
      myLearningStaffAssignEmailSubjectDisplay: resolveQmsEmailTemplate(mlAssignSubjTpl, mlAssignSubjDef),
      myLearningStaffAssignEmailBodyDisplay: resolveQmsEmailTemplate(mlAssignBodyTpl, mlAssignBodyDef),
      myLearningStaffRemoveEmailSubjectDisplay: resolveQmsEmailTemplate(mlRemoveSubjTpl, mlRemoveSubjDef),
      myLearningStaffRemoveEmailBodyDisplay: resolveQmsEmailTemplate(mlRemoveBodyTpl, mlRemoveBodyDef),
      transferOwnershipEmailSubjectDisplay: resolveQmsEmailTemplate(transferSubjTpl, transferSubjDef),
      transferOwnershipEmailBodyDisplay: resolveQmsEmailTemplate(transferBodyTpl, transferBodyDef),
      libraryAutoAssignEnabled:
        row?.LibraryAutoAssignEnabled === true ||
        row?.LibraryAutoAssignEnabled === 1 ||
        row?.libraryAutoAssignEnabled === true ||
        row?.libraryAutoAssignEnabled === 1,
      libraryAutoAssignMode:
        qmsConfigStr(row, "LibraryAutoAssignMode", "libraryAutoAssignMode") ||
        "AllExceptExcluded",
    };
  } catch (_e) {
    const fallback = buildQmsConfigFallback();
    return {
      ...fallback,
      libraryStaffAssignEmailSubjectDisplay: resolveQmsEmailTemplate(
        fallback.libraryStaffAssignEmailSubjectTemplate,
        fallback.libraryStaffAssignEmailSubjectDefault,
      ),
      libraryStaffAssignEmailBodyDisplay: resolveQmsEmailTemplate(
        fallback.libraryStaffAssignEmailBodyTemplate,
        fallback.libraryStaffAssignEmailBodyDefault,
      ),
      libraryStaffRemoveEmailSubjectDisplay: resolveQmsEmailTemplate(
        fallback.libraryStaffRemoveEmailSubjectTemplate,
        fallback.libraryStaffRemoveEmailSubjectDefault,
      ),
      libraryStaffRemoveEmailBodyDisplay: resolveQmsEmailTemplate(
        fallback.libraryStaffRemoveEmailBodyTemplate,
        fallback.libraryStaffRemoveEmailBodyDefault,
      ),
      reviewWorkflowEmailSubjectDisplay: resolveQmsEmailTemplate(
        fallback.reviewWorkflowEmailSubjectTemplate,
        fallback.reviewWorkflowEmailSubjectDefault,
      ),
      reviewWorkflowEmailBodyDisplay: resolveQmsEmailTemplate(
        fallback.reviewWorkflowEmailBodyTemplate,
        fallback.reviewWorkflowEmailBodyDefault,
      ),
      approveWorkflowEmailSubjectDisplay: resolveQmsEmailTemplate(
        fallback.approveWorkflowEmailSubjectTemplate,
        fallback.approveWorkflowEmailSubjectDefault,
      ),
      approveWorkflowEmailBodyDisplay: resolveQmsEmailTemplate(
        fallback.approveWorkflowEmailBodyTemplate,
        fallback.approveWorkflowEmailBodyDefault,
      ),
      myLearningStaffAssignEmailSubjectDisplay: resolveQmsEmailTemplate(
        fallback.myLearningStaffAssignEmailSubjectTemplate,
        fallback.myLearningStaffAssignEmailSubjectDefault,
      ),
      myLearningStaffAssignEmailBodyDisplay: resolveQmsEmailTemplate(
        fallback.myLearningStaffAssignEmailBodyTemplate,
        fallback.myLearningStaffAssignEmailBodyDefault,
      ),
      myLearningStaffRemoveEmailSubjectDisplay: resolveQmsEmailTemplate(
        fallback.myLearningStaffRemoveEmailSubjectTemplate,
        fallback.myLearningStaffRemoveEmailSubjectDefault,
      ),
      myLearningStaffRemoveEmailBodyDisplay: resolveQmsEmailTemplate(
        fallback.myLearningStaffRemoveEmailBodyTemplate,
        fallback.myLearningStaffRemoveEmailBodyDefault,
      ),
      transferOwnershipEmailSubjectDisplay: resolveQmsEmailTemplate(
        fallback.transferOwnershipEmailSubjectTemplate,
        fallback.transferOwnershipEmailSubjectDefault,
      ),
      transferOwnershipEmailBodyDisplay: resolveQmsEmailTemplate(
        fallback.transferOwnershipEmailBodyTemplate,
        fallback.transferOwnershipEmailBodyDefault,
      ),
    };
  }
}

export async function saveQmsConfig(payload, userData) {
  const requestPayload = buildQmsBasePayload(userData, {
    ActionType: "SaveConfig",
    LibraryStaffAssignEmailSubjectTemplate: String(
      payload?.libraryStaffAssignEmailSubjectTemplate ?? "",
    ).trim() || null,
    LibraryStaffAssignEmailBodyTemplate: String(
      payload?.libraryStaffAssignEmailBodyTemplate ?? "",
    ).trim() || null,
    LibraryStaffRemoveEmailSubjectTemplate: String(
      payload?.libraryStaffRemoveEmailSubjectTemplate ?? "",
    ).trim() || null,
    LibraryStaffRemoveEmailBodyTemplate: String(
      payload?.libraryStaffRemoveEmailBodyTemplate ?? "",
    ).trim() || null,
    ReviewWorkflowEmailSubjectTemplate: String(
      payload?.reviewWorkflowEmailSubjectTemplate ?? "",
    ).trim() || null,
    ReviewWorkflowEmailBodyTemplate: String(
      payload?.reviewWorkflowEmailBodyTemplate ?? "",
    ).trim() || null,
    ApproveWorkflowEmailSubjectTemplate: String(
      payload?.approveWorkflowEmailSubjectTemplate ?? "",
    ).trim() || null,
    ApproveWorkflowEmailBodyTemplate: String(
      payload?.approveWorkflowEmailBodyTemplate ?? "",
    ).trim() || null,
    MyLearningStaffAssignEmailSubjectTemplate: String(
      payload?.myLearningStaffAssignEmailSubjectTemplate ?? "",
    ).trim() || null,
    MyLearningStaffAssignEmailBodyTemplate: String(
      payload?.myLearningStaffAssignEmailBodyTemplate ?? "",
    ).trim() || null,
    MyLearningStaffRemoveEmailSubjectTemplate: String(
      payload?.myLearningStaffRemoveEmailSubjectTemplate ?? "",
    ).trim() || null,
    MyLearningStaffRemoveEmailBodyTemplate: String(
      payload?.myLearningStaffRemoveEmailBodyTemplate ?? "",
    ).trim() || null,
    TransferOwnershipEmailSubjectTemplate: String(
      payload?.transferOwnershipEmailSubjectTemplate ?? "",
    ).trim() || null,
    TransferOwnershipEmailBodyTemplate: String(
      payload?.transferOwnershipEmailBodyTemplate ?? "",
    ).trim() || null,
    LibraryAutoAssignEnabled:
      payload?.libraryAutoAssignEnabled === true ||
      payload?.libraryAutoAssignEnabled === 1
        ? 1
        : payload?.libraryAutoAssignEnabled === false ||
            payload?.libraryAutoAssignEnabled === 0
          ? 0
          : null,
    LibraryAutoAssignMode: String(payload?.libraryAutoAssignMode ?? "").trim() || null,
  });
  const res = await apiClient.post(E.CONFIG_SAVE, requestPayload);
  return {
    success: Number(res?.success) === 1,
    message: res?.message || "QMS configuration saved successfully.",
  };
}

function getLibraryAutoAssignDocumentsTable(response) {
  const ds = getApiDataset(response);
  if (!ds) return [];

  if (Array.isArray(ds.Tables)) {
    const named = ds.Tables.find(
      (table) =>
        String(table?.TableName || table?.tableName || "")
          .toLowerCase() === "libraryautoassigndocuments",
    );
    if (Array.isArray(named?.Rows)) return named.Rows;

    const storyIdx = ds.Tables.findIndex((table) => {
      const name = String(table?.TableName || table?.tableName || "").toLowerCase();
      return name === "successstory" || name === "table";
    });
    if (storyIdx >= 0 && ds.Tables[storyIdx + 1] && Array.isArray(ds.Tables[storyIdx + 1].Rows)) {
      return ds.Tables[storyIdx + 1].Rows;
    }

    for (const table of ds.Tables) {
      const rows = table?.Rows;
      if (
        Array.isArray(rows) &&
        rows.length &&
        (rows[0]?.DocumentGroupIdRef ||
          rows[0]?.documentGroupIdRef ||
          rows[0]?.DocumentTitle ||
          rows[0]?.documentTitle)
      ) {
        return rows;
      }
    }
  }

  const named = getFirstAvailableTable(ds, [
    "LibraryAutoAssignDocuments",
    "libraryAutoAssignDocuments",
  ]);
  if (Array.isArray(named)) return named;

  const keys = Object.keys(ds).filter((key) => Array.isArray(ds[key]));
  for (const key of keys) {
    if (key.toLowerCase() === "successstory") continue;
    const rows = ds[key];
    if (
      rows?.[0]?.DocumentGroupIdRef ||
      rows?.[0]?.documentGroupIdRef ||
      rows?.[0]?.DocumentTitle ||
      rows?.[0]?.documentTitle
    ) {
      return rows;
    }
  }

  const table1 = getFirstAvailableTable(ds, ["Table1"]);
  return Array.isArray(table1) ? table1 : [];
}

function isApiStorySuccess(response) {
  if (Number(response?.success) === 1) return true;
  const ds = getApiDataset(response);
  const story = getFirstAvailableTable(ds, ["SuccessStory", "successStory", "Table"]);
  const row = Array.isArray(story) ? story[0] : null;
  return Number(row?.Success ?? row?.success) === 1;
}

export async function saveLibraryAutoAssignPolicy(userData, policy = {}) {
  const res = await apiClient.post(
    E.CONFIG_SAVE,
    buildQmsBasePayload(userData, {
      ActionType: "SaveLibraryAutoAssignPolicy",
      LibraryAutoAssignEnabled:
        policy?.libraryAutoAssignEnabled === true ||
        policy?.libraryAutoAssignEnabled === 1
          ? 1
          : 0,
      LibraryAutoAssignMode: String(policy?.libraryAutoAssignMode || "AllExceptExcluded").trim(),
    }),
  );
  return {
    success: isApiStorySuccess(res),
    message: res?.message || "Library auto-assign policy saved successfully.",
  };
}

export async function fetchLibraryAutoAssignDocuments(userData) {
  const res = await apiClient.post(
    E.CONFIG_SAVE,
    buildQmsBasePayload(userData, { ActionType: "GetLibraryAutoAssignDocuments" }),
  );
  const rows = getLibraryAutoAssignDocumentsTable(res) || [];
  return {
    success: isApiStorySuccess(res),
    message: res?.message || "",
    rows,
  };
}

export async function saveLibraryAutoAssignRules(userData, excludedRows = []) {
  const rulesPayload = (excludedRows || [])
    .map((row) => {
      const groupId = String(row?.DocumentGroupIdRef || row?.documentGroupIdRef || "").trim();
      if (!groupId) return "";
      return `${groupId}|Exclude`;
    })
    .filter(Boolean)
    .join(",");

  const res = await apiClient.post(
    E.CONFIG_SAVE,
    buildQmsBasePayload(userData, {
      ActionType: "SaveLibraryAutoAssignRules",
      LibraryAutoAssignRules: rulesPayload,
    }),
  );
  return {
    success: isApiStorySuccess(res),
    message: res?.message || "Library auto-assign document rules saved successfully.",
  };
}

export async function syncLibraryAutoAssignAll(userData) {
  const res = await apiClient.post(
    E.CONFIG_SAVE,
    buildQmsBasePayload(userData, { ActionType: "SyncLibraryAutoAssignAll" }),
  );
  return {
    success: Number(res?.success) === 1,
    message: res?.message || "Library auto-assign sync completed.",
  };
}

/** Load full org list in one call (proc caps page size at 100). */
export const QMS_LIST_ALL_PAGE_SIZE = 100;

// —— Quiz V2 — question bank ——
export function fetchQuestionsV2(userData, { pageNumber = 1, pageSize = QMS_LIST_ALL_PAGE_SIZE, searchText = "" } = {}) {
  return postQms(
    "QUESTION_BANK_V2",
    buildQmsBasePayload(userData, {
      ActionType: "GetQuestions_V2",
      PageNumber: pageNumber,
      PageSize: pageSize,
      SearchText: searchText,
    }),
  );
}

export function fetchQuestionV2(userData, questionId) {
  return postQms(
    "QUESTION_BANK_V2",
    buildQmsBasePayload(userData, {
      ActionType: "GetQuestion_V2",
      QuestionId: questionId,
    }),
  );
}

export function saveQuestionV2(userData, body) {
  return postQms(
    "QUESTION_BANK_V2",
    buildQmsBasePayload(userData, {
      ActionType: "SaveQuestion_V2",
      ...body,
    }),
  );
}

export function saveQuestionsBulkV2(userData, questionsBulkJson) {
  return postQms(
    "QUESTION_BANK_V2",
    buildQmsBasePayload(userData, {
      ActionType: "SaveQuestionsBulk_V2",
      QuestionsBulkJson: questionsBulkJson,
    }),
  );
}

export function deleteQuestionV2(userData, questionId) {
  return postQms(
    "QUESTION_BANK_V2",
    buildQmsBasePayload(userData, {
      ActionType: "DeleteQuestion_V2",
      QuestionId: questionId,
    }),
  );
}

// —— Quiz V2 — quiz manager ——
export function fetchQuizzesV2(userData, { pageNumber = 1, pageSize = QMS_LIST_ALL_PAGE_SIZE, searchText = "" } = {}) {
  return postQms(
    "QUIZ_V2",
    buildQmsBasePayload(userData, {
      ActionType: "GetQuizzes_V2",
      PageNumber: pageNumber,
      PageSize: pageSize,
      SearchText: searchText,
    }),
  );
}

export function fetchQuizV2(userData, quizId) {
  return postQms(
    "QUIZ_V2",
    buildQmsBasePayload(userData, {
      ActionType: "GetQuiz_V2",
      QuizId: quizId,
    }),
  );
}

export function fetchAvailableQuestionsV2(userData) {
  return postQms(
    "QUIZ_V2",
    buildQmsBasePayload(userData, { ActionType: "GetAvailableQuestions_V2" }),
  );
}

export function saveQuizV2(userData, body) {
  return postQms(
    "QUIZ_V2",
    buildQmsBasePayload(userData, {
      ActionType: "SaveQuiz_V2",
      ...body,
    }),
  );
}

export function deleteQuizV2(userData, quizId) {
  return postQms(
    "QUIZ_V2",
    buildQmsBasePayload(userData, {
      ActionType: "DeleteQuiz_V2",
      QuizId: quizId,
    }),
  );
}
